import { luckysheetrefreshgrid } from "../global/refresh";
import { getPrevieDialogContent, getScreenshotByPageNum, getScreenshotBySheet, openInputPageDialog, printWindow } from "../utils/print";
import { patchImageContent, switchViewBtn } from "../utils/print";
import { openSelection, calculatePageRanges, getScreenshotByRange } from "../utils/print";

/**
 * @description 打印模块 ： 20251217 重构相关打印模块
 * @author weifeng
 * @email 1982392655@qq.com
 *
 * 待优化问题：
 *  1. ✅️ 如果打印选区，可能选区超过纸张大小，这里也需要进行拆分
 *  2. ✅️ 目前没有针对单元格图片进行处理
 *  3. ✅️ 目前打印页面时，判定有值单元格异常
 *  4. ❌️ 打印时，多出来的页面，没有与前打印页码缩放保持一致，导致页面缩放仅有几个单元格 （打印区域缩放不一致）[可以考虑使用渲染空单元格实现，后续验证]
 *  5. ❌️ 打印指定页码时，出现合并单元格后，支持手动调整打印范围（支持动态调整页面范围线）
 *  6. ✅️ 打印行截断问题(源于 css 样式问题，其实是打印完整的，只是构建的 printIframe 高度不够显示)
 */

// 定义全局打印参数
const printParams = {
    mode: "sheet", // 打印模式 sheet | areas | pages
    needToPreview: true, // 是否需要打开打印预览窗口,外部调用 API 时，不需要打开打印预览窗口
    printGridLine: true, // 是否打印网格线 - 这个参数是在打印预览窗口实现修改
    range: null, // 用户选区 - 1. 指代用户初始化时，用于初始化单元格选择器的默认选区；2. 也指代最终用户选定打印的区域
    pages: [], // 打印的页码范围 指的是用户打印指定页码，例如 1,3,4
    sheetRanges: [], // 存储当前sheet被拆分后的页面范围
    pageMargin: { left: 0, top: 0, right: 0, bottom: 0 }, // 当前的页边距
};

/**
 * @description 任何的打印模式，都需要调用此函数，这是打印功能的入口
 *      1. 打印当前sheet：handleSheetPrint
 *      2. 打印指定选区：handleAreasPrint
 *      4. 指定页码打印：handlePagesPrint
 * @param { Object } options 打印相关参数
 * @param {string} itemvalue 打印模式 sheet | areas | pages
 * @param {boolean} needToPreview 是否需要打开打印预览窗口,外部调用 API 时，不需要打开打印预览窗口
 * @param {string | Object } range 需要初始化的打印范围
 */
async function handlePrint(options = {}) {
    const { mode = "", needToPreview = true, range = "", printGridLine = true } = options;

    // 目前支持的打印方式为
    const modeList = ["sheet", "areas", "pages"];
    if (!mode || !modeList.includes(mode)) return console.warn("打印预览参数错误");

    // 重置参数
    printParams.mode = mode;
    printParams.range = range;
    printParams.pages = [];
    printParams.needToPreview = needToPreview;
    printParams.printGridLine = printGridLine;
    printParams.sheetRanges = [];

    try {
        let imageList = [];

        if (mode == "sheet") imageList = await handleSheetPrint(); // 打印当前sheet
        else if (mode == "areas") imageList = await handleAreasPrint(); // 打印指定选区
        else if (mode == "pages") imageList = await handlePagesPrint(); // 打印指定页码

        // 如果不需要打印弹窗，则直接调用 window 打印
        if (!needToPreview) {
            printWindow(imageList, printParams);
        } else {
            // 不然打开弹窗预览
            openPrintPreviewDialog(imageList);
        }
    } catch (error) {
        console.error(error);
    }
}

/**
 * @description 打印当前sheet - 这个函数不复杂，一切计算都在工具类中
 */
async function handleSheetPrint() {
    return new Promise(async (resolve, reject) => {
        try {
            // 1. 获取当前sheet的打印范围
            const sheetRanges = await getScreenshotBySheet();
            // 保存参数
            printParams.sheetRanges = sheetRanges;

            const imageList = await handleSheetPrintAfter(sheetRanges);
            resolve(imageList);
        } catch (error) {
            console.error(error);
        }
    });
}

/**
 * @description 打印当前sheet的后续操作
 */
async function handleSheetPrintAfter(sheetRanges) {
    try {
        // 2. 获取当前sheet的图片列表
        let imageList = [];
        for (let i = 0; i < sheetRanges.length; i++) {
            const range = sheetRanges[i];
            // 拿到的也是对应的打印范围，因此直接调用 range 的方法即可
            const image = await getScreenshotByRange({ range, printGridLine: printParams.printGridLine });
            imageList.push(image);
        }
        return Promise.resolve(imageList);
    } catch (error) {
        console.error(error);
    }
}

/**
 * @description 打印指定选区
 */
async function handleAreasPrint() {
    return new Promise((resolve, reject) => {
        try {
            // 调用数据验证控制器的方法，唤起单元格选择器，供用户选定打印选区
            openSelection(printParams.range, async ({ range }) => {
                // 这个 range 是最终用户选定打印的区域，需要记录这个参数
                printParams.range = range;
                const imageList = await handleAreasPrintAfter(range);
                resolve(imageList);
            });
        } catch (error) {
            console.error(error);
            reject(error);
        }
    });
}

/**
 * @description 选区打印拿到选区后续操作
 */
async function handleAreasPrintAfter(range) {
    try {
        const pageArray = calculatePageRanges(range);
        // console.log("pageArray:", pageArray);

        // 这里返回的就是当前选区在真实 A4 纸张下的打印需要的范围，循环进行打印，获取 imageList 即可
        let imageList = [];
        for (let i = 0; i < pageArray.length; i++) {
            const range = pageArray[i];
            const image = await getScreenshotByRange({ range, printGridLine: printParams.printGridLine });
            imageList.push(image);
        }

        // console.log("imageList:", imageList);
        if (imageList.length) return Promise.resolve(imageList);
        else return Promise.reject("选区打印范围异常");
    } catch (error) {
        console.error(error);
    }
}

/**
 * @description 打印指定页码
 */
async function handlePagesPrint() {
    return new Promise((resolve) => {
        // 1. 打开预览视图
        const $t = $(".luckysheet-print-viewBtn.luckysheet-print-viewPage");
        switchViewBtn($t);

        // 2. 等待预览视图加载完成
        setTimeout(() => {
            // 3. 打开输入页码弹窗，获取用户输入的页码
            openInputPageDialog(async (pages) => {
                // 这个pages 已经是用户输入的页码，需要记录这个参数
                printParams.pages = pages;
                const imageList = await handlePagesPrintAfter(pages);
                resolve(imageList);
            });
        }, 100);
    });
}

/**
 * @description 打印指定页码，拿到页码后续操作
 */
async function handlePagesPrintAfter(pages) {
    // 1. 获取图片列表
    const imageList = [];
    for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const image = await getScreenshotByPageNum({ pageNum: page, printGridLine: printParams.printGridLine });
        imageList.push(image);
    }

    // 2. 返回 imageList
    return Promise.resolve(imageList);
}

/**
 * @description 打开弹窗，进行打印预览，可进行打印参数配置，例如：是否打印网格线 | 页边距 | 缩放等
 */
function openPrintPreviewDialog(imageList, printGridLine = true) {
    if (!imageList || !imageList.length) return;

    // 1. 创建主体窗口容器
    const $dialog = $(getPrevieDialogContent());
    $dialog.appendTo($("body"));

    // 2. 填充图片
    patchImageContent($dialog, imageList);

    // 定义预览弹窗退出事件回调
    const handleExit = () => {
        $dialog.remove(); // 移除弹窗
        $(document).off("keydown.printPreview"); // 注销ESC事件
        // 重新渲染表格
        luckysheetrefreshgrid();
    };

    // 注册 ESC键 事件
    $(document).on("keydown.printPreview", function (e) {
        if (e.key === "Escape" || e.keyCode === 27) handleExit();
    });

    // 注册 退出预览 事件
    const exitBtn = $dialog.find(".luckysheet-print-preview-box-header button");
    exitBtn.off("click").click(handleExit);

    // 注册 网格线 事件 checkbox
    const checkbox = $dialog.find("#grid-line");
    // 用户如果自定义网格线状态，则需要设置其状态
    checkbox.prop("checked", printGridLine);
    checkbox.off("change").change(async function () {
        const isChecked = $(this).prop("checked");
        printParams.printGridLine = isChecked;

        // 更新打印状态，获取最新的 imageList
        imageList = await updateCheckPrint();

        // 更新页边距
        updatePageMargin();

        // 更新完状态后，需要重新初始化事件，避免网格线变化后，imageList 没有更新
        handleConfirmPrint($dialog, imageList);
    });

    // 注册页边距相关事件
    const mergeSelect = $dialog.find("#page-margin");

    mergeSelect.off("change").change(async function () {
        // 获取当前的值 这里拿到的值 就是 0 5 15 20 ，可以直接处理为 .luckysheet-print-preview-images-item 的 padding 值，目前暂时仅支持 4等边距
        // 应用在真是的 print 中，尽量通过 @print 媒体查询处理
        // 而媒体查询是通过 printWindow 处理的，因此，需要将这个参数进行保存，方便后续处理·
        const value = $(this).val();
        printParams.pageMargin = { left: value, right: value, top: value, bottom: value };

        // 更新页边距
        updatePageMargin();

        // 确认打印
        handleConfirmPrint($dialog, imageList);
    });

    // 这个是初始化第一次事件
    handleConfirmPrint($dialog, imageList);
}

/**
 * @description 更新打印状态 - 网格线状态
 */
async function updateCheckPrint() {
    // 这个函数的核心，就是重新获取 imageList 重新渲染当前打印预览的 image 列表
    const { mode, range, pages, sheetRanges } = printParams;

    let imageList = [];

    // 判定是什么模式
    if (mode == "sheet") {
        imageList = await handleSheetPrintAfter(sheetRanges); // 额外的参数都直接在内部调用 printParams 获取
    } else if (mode == "areas") {
        imageList = await handleAreasPrintAfter(range); // 额外的参数都直接在内部调用 printParams 获取
    } else if (mode == "pages") {
        imageList = await handlePagesPrintAfter(pages); // 额外的参数都直接在内部调用 printParams 获取
    }

    // 这里一定是重新渲染左侧列表即可
    const $dialog = $(".luckysheet-print-preview-box");
    if (!$dialog.length) return;

    // 填充图片
    patchImageContent($dialog, imageList);

    return imageList;
}

/**
 * @description 更新打印状态 - 页边距
 */
function updatePageMargin() {
    // 直接从 printParams 中获取
    const { left = 0, right = 0, top = 0, bottom = 0 } = printParams.pageMargin;

    // 这里一定是重新渲染左侧列表即可
    const $dialog = $(".luckysheet-print-preview-box");
    if (!$dialog.length) return;

    // 这里添加 padding 值，重新更新即可
    $dialog.find(".luckysheet-print-preview-images-item").css({
        padding: `${top}px ${right}px ${bottom}px ${left}px`,
    });
}

/**
 * @description 将确认打印的事件单独抽离出来
 * @param { HTMLElement } $dialog
 */
function handleConfirmPrint($dialog, imageList) {
    if (!$dialog || !imageList || !imageList.length) return;

    // 注册 确认打印按钮 事件
    const confirmBtn = $dialog.find(".luckysheet-print-preview-settings-confirm button");

    if (!confirmBtn) return;

    confirmBtn.off("click").click(() => printWindow(imageList, printParams));
}

export { handlePrint };
